import { Link as RouterLink } from 'react-router-dom';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { formatEntityTagsLabel } from '@/features/shared/tags';
import { s } from '@/theme/tokens';
import { InfoField } from '@/ui/composites/InfoField';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { LayoutGrid, LayoutGridItem } from '@/ui/layout/LayoutGrid';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Link, Surface } from '@/ui/primitives';
import type { BuildDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

export type BuildDetailsPanelsProps = {
  build: BuildDto;
};

export const BuildDetailsPanels = ({ build }: BuildDetailsPanelsProps): ReactElement => {
  const noDataLabel = 'No data';
  const buildRunNumber = resolveBuildRunNumber(build.buildKey ?? null);
  const runLabel = buildRunNumber !== null ? `#${buildRunNumber}` : noDataLabel;
  const projectLabel = build.projectName ?? build.projectSlug ?? build.projectId;
  const pipelineLabel = build.pipelineName ?? build.pipelineId ?? noDataLabel;
  const pipelineSlug = build.pipelineId
    ? resolvePipelineSlug({
        id: build.pipelineId,
        name: build.pipelineName ?? build.pipelineId,
      })
    : null;
  const pipelineHref = pipelineSlug ? `/pipelines/${pipelineSlug}` : null;
  const buildHref =
    pipelineSlug && buildRunNumber !== null
      ? `/pipelines/${pipelineSlug}/runs/${buildRunNumber}`
      : null;
  const startedAtLabel = formatDate(build.startedAt, { includeTime: true }) || noDataLabel;
  const finishedAtLabel = formatDate(build.finishedAt, { includeTime: true }) || noDataLabel;
  const durationLabel =
    build.startedAt && build.finishedAt
      ? formatDuration(build.finishedAt.getTime() - build.startedAt.getTime())
      : noDataLabel;
  const sourceLabel = build.source ?? noDataLabel;
  const branchLabel = build.branch ?? noDataLabel;
  const commitLabel = build.commitSha ?? noDataLabel;
  const jobUrlLabel = build.jobUrl ?? noDataLabel;
  const tagsLabel = formatEntityTagsLabel(build.tags);

  return (
    <Section paddingY="md">
      <Surface padding="lg" border>
        <LayoutGrid gap="md">
          <LayoutGridItem
            xs={12}
            sm={4}
            sx={{ borderRight: 0.5, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingRight: s('md'), width: '100%' }}>
              <InfoField
                label="Build"
                value={
                  buildHref ? (
                    <Link component={RouterLink} to={buildHref} underline="hover" color="inherit">
                      {runLabel}
                    </Link>
                  ) : (
                    runLabel
                  )
                }
                gap="none"
              />
              <InfoField label="Project" value={projectLabel} gap="none" />
              <InfoField
                label="Pipeline"
                value={
                  pipelineHref ? (
                    <Link
                      component={RouterLink}
                      to={pipelineHref}
                      underline="hover"
                      color="inherit"
                    >
                      {pipelineLabel}
                    </Link>
                  ) : (
                    pipelineLabel
                  )
                }
                gap="none"
              />
              <InfoField label="Tags" value={tagsLabel} gap="none" />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem
            xs={12}
            sm={4}
            sx={{ borderRight: 0.5, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingX: s('md'), width: '100%' }}>
              <InfoField label="Build Key" value={build.buildKey ?? noDataLabel} gap="none" />
              <InfoField label="Source" value={sourceLabel} gap="none" />
              <InfoField label="Branch" value={branchLabel} gap="none" />
              <InfoField label="Commit SHA" value={commitLabel} gap="none" />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem xs={12} sm={4}>
            <Stack gap="sm" sx={{ paddingLeft: s('md') }}>
              <InfoField label="Started" value={startedAtLabel} gap="none" />
              <InfoField label="Finished" value={finishedAtLabel} gap="none" />
              <InfoField label="Duration" value={durationLabel} gap="none" />
              <InfoField
                label="Job URL"
                value={
                  build.jobUrl ? (
                    <Link href={build.jobUrl} target="_blank" rel="noopener noreferrer">
                      {jobUrlLabel}
                    </Link>
                  ) : (
                    jobUrlLabel
                  )
                }
                gap="none"
              />
            </Stack>
          </LayoutGridItem>
        </LayoutGrid>
      </Surface>
    </Section>
  );
};
